import { Global, css } from '@emotion/react'
import { Link } from 'gatsby'
import { type ComponentProps } from 'react'

import { DatoLinkIcon } from '@/features/dato-link'
import { mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { useClassNotesContext } from '../../context/classNotesContext'

type Props = ComponentProps<'div'> & {
  previousYear?: { year: number; firstSlug: string } | undefined
  nextYear?: { year: number; firstSlug: string } | undefined
  classYear?: number | 'Milestones' | 'Faculty/Staff Notes'
}

export const CNNCollapsed = ({
  previousYear,
  nextYear,
  classYear,
  ...props
}: Props) => {
  const { setExpanded } = useClassNotesContext()
  const styles = {
    global: css`
      :root {
        --cnn-height: max(calc(var(--fs-30) * 1.1 + 1rem), 3.5rem);
      }
    `,
    collapsedNav: css`
      grid-column: 1 / -1;
      position: sticky;
      z-index: 3;
      top: var(--nav-height-min);
      background-color: ${colors.gray10};
      padding: 0.5rem var(--margin);
      color: #fff;
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      > div {
        display: flex;
        align-items: center;
        &:nth-of-type(3) {
          justify-content: flex-end;
        }
      }
      ${mq().ms} {
        &:before {
          content: '';
          flex: 1;
        }
      }
    `,
    pageTitle: css`
      font-size: var(--fs-18);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 500;
      a {
        color: currentColor;
        text-decoration: none;
        @media (hover: hover) {
          &:hover {
            color: ${colors.redLight};
          }
        }
      }
      ${mq().ms} {
        && {
          display: none;
        }
      }
    `,
    currentYear: css`
      font-size: var(--fs-30);
      font-family: var(--ff-display-optical);
      font-weight: 400;
      line-height: 1;
      margin-bottom: 0.1em;
    `,
    arrow: css`
      cursor: pointer;
      color: #ffffffaa;
      --font-weight: 500;
      &[aria-disabled='true'] {
        opacity: 0.25;
        pointer-events: none;
      }
      > div {
        width: fit-content;
        height: fit-content;
      }
      width: 1em;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      line-height: 1;
      [data-back] {
        transform: scaleX(-1);
      }
      @media (hover: hover) {
        &:hover {
          color: ${colors.redLight};
        }
      }
    `,
    button: css`
      display: flex;
      width: 2.5rem;
      height: 2.5rem;
      padding: 0.25rem;
      box-sizing: border-box;
      color: hsla(0, 0%, 100%, 0.75);
      margin-right: -0.25rem;
      svg {
        flex: 1;
        display: flex;
      }
      rect {
        fill: currentColor;
      }
      @media (hover: hover) {
        &:hover {
          color: ${colors.redLight};
        }
      }
    `,
  }

  return (
    <nav
      css={styles.collapsedNav}
      {...props}
    >
      <Global styles={styles.global} />
      <div css={styles.pageTitle}>
        <Link to="/class-notes/">Class Notes</Link>
      </div>
      <div>
        {classYear !== 'Milestones' && (
          <Link
            to={`/${previousYear?.firstSlug || ''}`}
            aria-disabled={!previousYear}
            css={styles.arrow}
          >
            <DatoLinkIcon
              iconType={'ARROW'}
              data-back
            />
          </Link>
        )}
        <div css={styles.currentYear}>
          {classYear === 'Milestones'
            ? classYear
            : `Class of ${classYear}`}
        </div>
        {classYear !== 'Milestones' && (
          <Link
            to={`/${nextYear?.firstSlug || ''}`}
            aria-disabled={!nextYear}
            css={styles.arrow}
          >
            <DatoLinkIcon iconType={'ARROW'} />
          </Link>
        )}
      </div>
      <div>
        <button
          css={styles.button}
          onClick={() => setExpanded(true)}
        >
          <svg viewBox="0 0 20 20">
            <rect
              width="4"
              height="4"
            />
            <rect
              x="8"
              width="4"
              height="4"
            />
            <rect
              x="16"
              width="4"
              height="4"
            />
            <rect
              y="8"
              width="4"
              height="4"
            />
            <rect
              x="8"
              y="8"
              width="4"
              height="4"
            />
            <rect
              x="16"
              y="8"
              width="4"
              height="4"
            />
            <rect
              y="16"
              width="4"
              height="4"
            />
            <rect
              x="8"
              y="16"
              width="4"
              height="4"
            />
            <rect
              x="16"
              y="16"
              width="4"
              height="4"
            />
          </svg>
        </button>
      </div>
    </nav>
  )
}
